import { useNavigate } from 'react-router-dom';
import { ArrowLeft, Heart, ArrowUp, CheckCircle, AlertTriangle, Sparkles } from 'lucide-react';
import { Button } from '@/components/ui/button';
import Navigation from '@/components/Navigation';

function NeckCareGuide() {
  const navigate = useNavigate();

  const exercises = [
    { name: "Neck Tilt", reps: "Hold 5 seconds each side", steps: ["Tilt head toward right shoulder", "Feel stretch on left side", "Repeat on other side", "Do 5 times each side"] },
    { name: "Chin to Ceiling", reps: "Hold 10 seconds", steps: ["Sit or stand tall", "Tilt head back, chin to ceiling", "Feel stretch in front of neck", "Do 10 times"] },
    { name: "Neck Rotation", reps: "5 circles each direction", steps: ["Slowly rotate head in circle", "Keep shoulders relaxed", "Reverse direction", "Move slowly and controlled"] },
    { name: "Jaw Release", reps: "Hold 5 seconds", steps: ["Open mouth wide", "Touch tongue to roof of mouth", "Lower jaw while keeping tongue up", "Repeat 10 times"] }
  ];

  const applicationTips = [
    { tip: "Always apply products upward", why: "Gravity already pulls skin down - don't help it" },
    { tip: "Don't forget behind ears", why: "This area ages too and often gets neglected" },
    { tip: "Include your décolletage", why: "Chest skin is just as delicate as face skin" },
    { tip: "Use same products as face", why: "Your neck deserves the same active ingredients" },
    { tip: "Be gentle - no tugging", why: "Neck skin is thin and easily damaged" },
    { tip: "Apply SPF to neck daily", why: "Sun damage causes crepey texture" }
  ];

  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      
      <header className="bg-white shadow-sm border-b border-primary/10">
        <div className="container mx-auto px-6 py-6">
          <Button onClick={() => navigate(-1)} variant="ghost" className="mb-4 text-secondary-700 hover:text-primary">
            <ArrowLeft className="mr-2 w-5 h-5" /> Back
          </Button>
        </div>
      </header>

      <main className="container mx-auto px-6 py-8 max-w-4xl">
        <div className="text-center mb-8">
          <span className="inline-block px-4 py-1 bg-purple-100 text-purple-700 rounded-full text-sm font-semibold mb-4">
            Week 3 • Day 6 Guide
          </span>
          <h1 className="text-4xl md:text-5xl font-serif font-bold text-secondary-900 mb-4">
            Neck and Chest Treatment
          </h1>
          <p className="text-xl text-secondary-600">The Often-Forgotten Youth Zone</p>
        </div>

        {/* Hero */}
        <section className="bg-gradient-to-r from-purple-500 to-violet-500 rounded-2xl p-8 text-white mb-8">
          <div className="flex items-start gap-4">
            <Heart className="w-12 h-12 flex-shrink-0 mt-1" />
            <div>
              <h3 className="font-bold text-2xl mb-2">Don't Let Your Neck Give Away Your Age</h3>
              <p className="text-purple-100 text-lg">
                The neck is one of the first places to show aging, yet it's often neglected in skincare routines. 
                Today we're changing that. Treat your neck like the extension of your face it is.
              </p>
            </div>
          </div>
        </section>

        {/* Why It Matters */}
        <section className="bg-white rounded-2xl p-8 shadow-md border border-gray-100 mb-6">
          <h2 className="text-2xl font-serif font-bold text-secondary-900 mb-4">Why Neck Care Matters</h2>
          <div className="grid md:grid-cols-3 gap-4">
            {[
              { title: "Thin Skin", desc: "Neck skin is thinner than face skin, making it prone to sagging and wrinkles" },
              { title: "Fewer Oil Glands", desc: "Less natural moisture means it dries out and ages faster" },
              { title: "Tech Neck", desc: "Looking at phones creates horizontal lines that deepen over time" }
            ].map((item, i) => (
              <div key={i} className="p-4 bg-purple-50 rounded-xl text-center">
                <h3 className="font-bold text-purple-700 mb-2">{item.title}</h3>
                <p className="text-sm text-secondary-600">{item.desc}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Application Tips */}
        <section className="bg-gradient-to-br from-purple-50 to-violet-50 rounded-2xl p-8 border-2 border-purple-200 mb-6">
          <h2 className="text-2xl font-serif font-bold text-secondary-900 mb-6 flex items-center gap-2">
            <ArrowUp className="w-6 h-6 text-purple-500" />
            How to Apply Products
          </h2>
          <div className="grid md:grid-cols-2 gap-4">
            {applicationTips.map((item, index) => (
              <div key={index} className="bg-white rounded-xl p-4">
                <h3 className="font-bold text-purple-700 mb-1 flex items-center gap-2">
                  <CheckCircle className="w-4 h-4" />
                  {item.tip}
                </h3>
                <p className="text-sm text-secondary-600 ml-6">{item.why}</p>
              </div>
            ))}
          </div>
        </section>

        {/* Neck Exercises */}
        <section className="bg-white rounded-2xl p-8 shadow-md border border-gray-100 mb-6">
          <h2 className="text-2xl font-serif font-bold text-secondary-900 mb-6 flex items-center gap-2">
            <Sparkles className="w-6 h-6 text-purple-500" />
            Neck Firming Exercises
          </h2>
          <div className="space-y-4">
            {exercises.map((exercise, index) => (
              <div key={index} className="bg-purple-50 rounded-xl p-5">
                <div className="flex justify-between items-start mb-3">
                  <h3 className="text-lg font-bold text-purple-700">{exercise.name}</h3>
                  <span className="text-sm bg-purple-200 text-purple-700 px-3 py-1 rounded-full">{exercise.reps}</span>
                </div>
                <ol className="grid md:grid-cols-2 gap-2">
                  {exercise.steps.map((step, i) => (
                    <li key={i} className="flex items-center gap-2 text-sm text-secondary-700">
                      <span className="w-5 h-5 bg-purple-500 text-white rounded-full flex items-center justify-center text-xs flex-shrink-0">{i + 1}</span>
                      {step}
                    </li>
                  ))}
                </ol>
              </div>
            ))}
          </div>
        </section>

        {/* Sleep Tip */}
        <section className="bg-white rounded-2xl p-8 shadow-md border border-gray-100">
          <h2 className="text-2xl font-serif font-bold text-secondary-900 mb-4 flex items-center gap-2">
            <AlertTriangle className="w-6 h-6 text-amber-500" />
            Tonight's Challenge: Sleep on Your Back
          </h2>
          <p className="text-secondary-700 mb-4">
            Side and stomach sleeping creates creases in neck and chest skin that become permanent over time. 
            Try sleeping on your back tonight—or at least start that way.
          </p>
          <div className="bg-amber-50 rounded-xl p-4 border border-amber-200">
            <p className="text-secondary-800 text-sm">
              <strong>Tip:</strong> Place a pillow under your knees to make back sleeping more comfortable. 
              Consider a silk pillowcase to reduce friction if you do turn during the night.
            </p>
          </div>
        </section>
      </main>
    </div>
  );
}

export default NeckCareGuide;
